/* eslint-disable no-underscore-dangle */
import { setIsStaff } from 'pages/SignIn/redux';
import { useLocalRoomUpdate } from 'pages/Hospitality/hooks';
import { selectRooms } from 'pages/Hospitality/redux';
import { useLogs } from 'pages/Logs/hooks';
import {
  BranchFiles,
  getBranchFile,
  selectBranchFiles,
  selectLogs,
  setBranchFiles,
  setIsLoadingLogs,
  setLogs,
  setLogsChecked,
} from 'pages/Logs/redux';
import { rewriteLogsForDepartmentRename } from 'pages/Logs/logUtils';
import { aggregateLogsToMetricLogs } from 'pages/Metrics/aggregateMetricLogs';
import { setMetricLogs } from 'pages/Metrics/redux';
import { useUpdateSettings } from 'pages/Settings/hooks';
import { setCRMSettings, addOrUpdateCustomer, getInfo, Customer } from 'pages/Settings/redux';
import {
  HotelData, selectHotelData, setHotelData, setIsSocketConnected,
  setSocket
} from 'pages/redux';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'utils/toast';
import { selectUsername, useCreds } from 'redux/global';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { store } from 'redux/store';
import * as io from 'socket.io-client';
import { getApiUrl } from 'utils';
import { syncAppDbCache } from 'utils/syncAppDbCache';
import { useDbCache } from 'hooks/useDbCache';
import { fetchManagerReports } from 'pages/Reporting/redux';
import { selectUserEmail } from 'pages/Hospitality/redux';

const socket = io.connect(
  window.location.hostname === (process.env.REACT_APP_MOBILE_APP_URL || '').split(':8200').join('')
    .split('http://').join('').split('/').join('') ? process.env.REACT_APP_MOBILE_APP_URL || '' :
    getApiUrl(),
  {
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: 20,
    reconnectionDelay: 1000,
  }
);

export const useHotel = () => {
  const dispatch = useAppDispatch();

  const updateHotel = (hotelData1: HotelData) => {
    const current = (store.getState() as any).views?.hotelData as HotelData | null;
    if (!current || !hotelData1 || typeof hotelData1 !== 'object') return;
    // Shallow merge; inventory/prefs patches from sockets replace those fields.
    dispatch(setHotelData({ ...current, ...hotelData1 }));
  };
  return { updateHotel };
};

export const useSocketEmit = () => {
  const hotelData = useAppSelector(selectHotelData);

  const socketEmit = (type: string, data: Record<string, any>) => {
    socket.emit(type, {
      roomId: `room${hotelData?.id}`,
      ...data,
      senderId: socket.id
    });
  };

  return socketEmit;
};
export const useSocketTypeEmit = () => {
  const hotelData = useAppSelector(selectHotelData);

  const settingsEmit = (part: Record<string, any>) => {
    const settings = { ...part };
    // Data-URI photos blow past socket.io's packet cap and the whole update is dropped.
    if (Array.isArray(settings.users)) {
      settings.users = settings.users.map((user: { photo?: string | null }) => {
        const photo = user?.photo;
        if (typeof photo === 'string' && (photo.startsWith('data:') || photo.startsWith('blob:'))) {
          return { ...user, photo: null };
        }
        return user;
      });
    }
    socket.emit('update_settings', {
      roomId: `room${hotelData?.id}`,
      settings,
      senderId: socket.id
    });
  };

  return { settingsEmit };
};

export const useDbCacheScope = () => {
  useDbCache();
};

export const useParentEffects = () => {
  useDbCacheScope();
  const location = useLocation();
  const logs = useAppSelector(selectLogs);
  const dispatch = useAppDispatch();
  const hotelData = useAppSelector(selectHotelData);
  const branchFiles = useAppSelector(selectBranchFiles);
  const rooms = useAppSelector(selectRooms);
  const savedUsername = useAppSelector(selectUsername);
  const userEmail = useAppSelector(selectUserEmail);
  const { addLocalRoom, editLocalRoom, deleteLocalRooms } = useLocalRoomUpdate();
  const {
    deleteLocalLog, updateLocalLog, fetchBranchFiles, updateLocalBranch, deleteLocalBranch,
    deleteLocalBranchFile, deleteLocalBranchFiles,
    deleteLocalLogType, logGroups
  } = useLogs();
  const { updateHotel } = useHotel();
  const { updateLocalSettings } = useUpdateSettings();
  const { logout } = useCreds();

  useEffect(() => {
    // ===== GLOBAL SELF-MESSAGE BLOCK =====
    const originalOn = socket.on.bind(socket);

    // eslint-disable-next-line no-unused-vars
    socket.on = (event: string, handler: (...args: any[]) => void) => {
      return originalOn(event, (data: any, ...rest: any[]) => {
        // ignore messages sent by this same socket
        if (
          data &&
          typeof data === 'object' &&
          data.senderId === socket.id
        ) {
          return;
        }

        // optional unwrap if you wrapped payloads
        if (data && typeof data === 'object' && 'payload' in data) {
          return handler(data.payload, ...rest);
        }

        return handler(data, ...rest);
      });
    };
    // =====================================

    socket.on('connect_error', () => {
      dispatch(setIsSocketConnected(false));
    });

    socket.on('connect', () => {
      dispatch(setSocket(socket));
      dispatch(setIsSocketConnected(true));

      // Ensure the client rejoins its room after a reconnect/backend restart.
      // Use the Redux store to get the latest hotel id in case it changed.
      const hotelId = (store.getState() as any).views?.hotelData?.id;
      if (hotelId) {
        try {
          socket.emit('join_room', `room${hotelId}`);
        } catch (e) {
          // ignore emit errors
        }
      }
    });

    socket.on('get_added_room', (room) => {
      addLocalRoom(room);
    });

    socket.on('get_edited_room', (rooms1) => {
      editLocalRoom(rooms1);
    });

    socket.on('get_deleted_rooms', (ids) => {
      deleteLocalRooms(ids);
    });

    socket.on('get_revoked_staff', (username) => {
      if (savedUsername?.replace(/\s+/g, '') === username.replace(/\s+/g, '')) {
        toast('Your access is denied', { type: 'error' });
        dispatch(setIsStaff(false));
        dispatch(logout());
      }
    });

    socket.on('get_added_log', (logEntry) => {
      const { logs: logsH, checked } = store.getState().logs;
      if (logsH && logEntry?.id != null
        && !logsH.find((log) => log.id.toString() === logEntry.id.toString())) {
        dispatch(setLogs([...logsH, logEntry]));
        syncAppDbCache();
      }
      // Keep walk-in audits visible for all staff when walk activity arrives
      if (logEntry?.type === 'Walk in' || logEntry?.type === 'Walk out') {
        const current: string[] = checked || [];
        const next = [
          ...current.filter((c: string) => c !== logEntry.type && c !== 'Walk-ins'),
          logEntry.type,
        ];
        if (next.length !== current.length
          || next.some((c, i) => c !== current[i])) {
          dispatch(setLogsChecked(next));
        }
      }
    });

    socket.on('get_deleted_log', (logId) => {
      if (store.getState().logs.logs) deleteLocalLog(logId);
    });

    socket.on('get_deleted_log_type', (type) => {
      if (store.getState().logs.logs) deleteLocalLogType(type);
    });

    socket.on('get_updated_log', (log) => {
      if (store.getState().logs.logs) updateLocalLog(log);
    });

    socket.on('get_updated_branchFiles', async (payload) => {
      if (!logs) return
      const { action, branch, sheetName } = payload || {}
      if (!branch || !action) return

      if (action === 'deleted') {
        if (sheetName) {
          deleteLocalBranchFile(branch, sheetName)
        }
        return
      }

      if (action === 'deleted_branch') {
        deleteLocalBranchFiles(branch)
        return
      }

      if (action === 'renamed_branch') {
        const previousName = typeof payload?.previousName === 'string'
          ? payload.previousName
          : ''
        const branchName = typeof branch === 'string' ? branch : ''
        if (previousName && branchName && branchFiles?.[previousName]) {
          const nextFiles: BranchFiles = {
            ...branchFiles,
            [branchName]: branchFiles[previousName],
          }
          delete nextFiles[previousName]
          dispatch(setBranchFiles(nextFiles))
        } else if (logs) {
          fetchBranchFiles(true)
        }
        // Mirror DB bulk rename of log department tags for other clients.
        if (previousName && branchName) {
          const { logs: currentLogs, checked } = store.getState().logs
          if (currentLogs) {
            const rewritten = rewriteLogsForDepartmentRename(
              currentLogs,
              previousName,
              branchName,
            )
            if (rewritten) dispatch(setLogs(rewritten))
          }
          if (checked?.some((c: string) => c === previousName)) {
            dispatch(setLogsChecked(
              checked.map((c: string) => (c === previousName ? branchName : c)),
            ))
          }
        }
        return
      }

      if (action === 'added' && sheetName) {
        const res = await dispatch(getBranchFile(branch, sheetName))
        if (res?.status !== 'success') return
        const newBranchFiles = {
          ...(branchFiles || {}),
          [branch]: {
            ...((branchFiles || {})[branch] || {}),
            [sheetName]: res.data.sheet
          }
        }
        dispatch(setBranchFiles(newBranchFiles))
        return
      }

      if (logs) fetchBranchFiles(true);
    });

    socket.on('get_updated_branch', (payload) => {
      const branch = payload?.branch || payload;
      const previousName = payload?.previousName;
      if (!branch) return;
      updateLocalBranch(branch, previousName);
    });

    socket.on('get_deleted_branch', (branch) => {
      deleteLocalBranch(branch);
    });

    socket.on('get_updated_hotel', (hotelData1) => {
      updateHotel(hotelData1);
      syncAppDbCache();
    });

    socket.on('get_updated_settings', (settings) => {
      updateLocalSettings(settings);
    });

    socket.on('get_updated_crm_settings', async () => {
      try {
        const res = await dispatch(getInfo());
        if (res?.status === 'success' && res.data) {
          // `info` endpoint may return the full HotelInfo; prefer its crmSettings field
          const incomingSettings = res.data.crmSettings ?? res.data;
          // console.log('incoming CRM settings', incomingSettings);
          if (incomingSettings) {
            dispatch(setCRMSettings(incomingSettings));
            syncAppDbCache();
          }
        }
      } catch (e) {
        // non-fatal; ignore
      }
    });

    socket.on('get_customer_deltas', ({ customers }) => {
      (customers || []).forEach((customer: Customer) => {
        dispatch(addOrUpdateCustomer(customer));
      });
      syncAppDbCache();
    });

    // Recipient refreshes inbox when a new/cleared manager report arrives.
    socket.on('get_updated_manager_reports', (payload: { toEmail?: string }) => {
      const to = String(payload?.toEmail || '').toLowerCase();
      const me = String(userEmail || '').toLowerCase();
      if (to && me && to !== me) return;
      dispatch(fetchManagerReports());
    });

    return () => {
      socket.off();
      socket.on = originalOn; // restore original
    };
  }, [socket, rooms, logs, hotelData, branchFiles, userEmail]);

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('main_cont')?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 140);
  }, [location]);

  // Rebuild metric buckets whenever raw logs change. Do NOT flip
  // isLoadingLogs back to true here — that unmounts the page (holdChildren)
  // and looked like repeated refreshes when branch-file logs merged in.
  useEffect(() => {
    if (!logs) return;
    dispatch(setMetricLogs(aggregateLogsToMetricLogs(logs)));
    dispatch(setIsLoadingLogs(false));
  }, [logs, dispatch]);
};
